import { useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, Clock, AlertTriangle, Ban, ChevronDown, FlaskConical, Upload, Check } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Alert';
import { useAuth } from '../../contexts/AuthContext';
import { useStore } from '../../contexts/StoreContext';
import {
  verificationApi,
  type VerificationCountry,
  type SaudiDocumentType,
} from '../../api/endpoints/verification';

const MAX_DOC_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIME = 'image/png,image/avif,image/webp,image/jpeg,image/gif,application/pdf';

interface CountryOption {
  value: VerificationCountry;
  flag: string;
  nameKey: string;
}

const COUNTRIES: CountryOption[] = [
  { value: 'saudi', flag: '🇸🇦', nameKey: 'verification.countries.saudi' },
  { value: 'uae', flag: '🇦🇪', nameKey: 'verification.countries.uae' },
  { value: 'egypt', flag: '🇪🇬', nameKey: 'verification.countries.egypt' },
  { value: 'jordan', flag: '🇯🇴', nameKey: 'verification.countries.jordan' },
  { value: 'lebanon', flag: '🇱🇧', nameKey: 'verification.countries.lebanon' },
  { value: 'kuwait', flag: '🇰🇼', nameKey: 'verification.countries.kuwait' },
  { value: 'qatar', flag: '🇶🇦', nameKey: 'verification.countries.qatar' },
  { value: 'bahrain', flag: '🇧🇭', nameKey: 'verification.countries.bahrain' },
  { value: 'oman', flag: '🇴🇲', nameKey: 'verification.countries.oman' },
  { value: 'morocco', flag: '🇲🇦', nameKey: 'verification.countries.morocco' },
  { value: 'algeria', flag: '🇩🇿', nameKey: 'verification.countries.algeria' },
  { value: 'tunisia', flag: '🇹🇳', nameKey: 'verification.countries.tunisia' },
  { value: 'iraq', flag: '🇮🇶', nameKey: 'verification.countries.iraq' },
  { value: 'syria', flag: '🇸🇾', nameKey: 'verification.countries.syria' },
  { value: 'yemen', flag: '🇾🇪', nameKey: 'verification.countries.yemen' },
  { value: 'palestine', flag: '🇵🇸', nameKey: 'verification.countries.palestine' },
  { value: 'sudan', flag: '🇸🇩', nameKey: 'verification.countries.sudan' },
  { value: 'libya', flag: '🇱🇾', nameKey: 'verification.countries.libya' },
  { value: 'other', flag: '🌍', nameKey: 'verification.countries.other' },
];

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

type StatusKind = 'verified' | 'pending' | 'failed' | 'blocked' | 'form';

export function VerificationPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentStore } = useStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [country, setCountry] = useState<VerificationCountry>('saudi');
  const [countryOpen, setCountryOpen] = useState(false);
  const [saudiDocType, setSaudiDocType] = useState<SaudiDocumentType | ''>('');
  const [fullName, setFullName] = useState('');
  const [agree, setAgree] = useState(true);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [usernameProofFile, setUsernameProofFile] = useState<File | null>(null);
  const [faceProofFile, setFaceProofFile] = useState<File | null>(null);
  const [nationalIdFile, setNationalIdFile] = useState<File | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [showRetryForm, setShowRetryForm] = useState(false);
  const countryWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!countryWrapRef.current) return;
      if (!countryWrapRef.current.contains(e.target as Node)) setCountryOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const status = useMemo<StatusKind>(() => {
    if (justSubmitted) return 'pending';
    if (user?.verifiedIdentity) return 'verified';
    if (user?.verificationProgress?.blocked) return 'blocked';
    if (user?.verificationProgress?.state === 'pending') return 'pending';
    if (user?.verificationProgress?.state === 'failed' && !showRetryForm) return 'failed';
    return 'form';
  }, [user, justSubmitted, showRetryForm]);

  const step = useMemo(() => {
    if (country !== 'saudi') return country ? 2 : 1;
    if (!saudiDocType) return 1;
    return 2;
  }, [country, saudiDocType]);

  // Which document sets this merchant may use. Discord-signup accounts get the
  // relaxed Saudi set (national ID only) as the default, and everyone who is
  // not Saudi has to prove the document belongs to them.
  const { data: options } = useQuery({
    queryKey: ['verification', 'options'],
    queryFn: verificationApi.options,
  });

  const canUseDiscordPath = options?.discord.canUseDiscordPath === true;
  const proofLabel = options?.proofLabel || '';

  // Default Saudi merchants who signed up through Discord onto the Discord set,
  // once, without overriding a choice they have already made.
  const [docTypeDefaulted, setDocTypeDefaulted] = useState(false);
  useEffect(() => {
    if (docTypeDefaulted || !options) return;
    if (country === 'saudi' && options.defaultSaudiDocumentType && !saudiDocType) {
      setSaudiDocType(options.defaultSaudiDocumentType);
    }
    setDocTypeDefaulted(true);
  }, [options, country, saudiDocType, docTypeDefaulted]);

  // The proof is required for every non-Saudi request, and for the Saudi
  // Discord set where it is the only thing tying the ID to the person.
  const needsUsernameProof = country !== 'saudi' || saudiDocType === 'discord';

  const showDocumentFields = country !== 'saudi' || !!saudiDocType;

  const requirementsText = useMemo(() => {
    if (country === 'saudi' && saudiDocType === 'discord') {
      return t('verification.requirements.saudiDiscord', 'Required: one photo of your national ID next to your Zero username');
    }
    if (country === 'saudi' && saudiDocType === 'freelance') {
      return t('verification.requirements.saudiFreelance', 'Required: Freelance license + national ID');
    }
    if (country === 'saudi' && saudiDocType === 'commercial') {
      return t('verification.requirements.saudiCommercial', 'Required: Commercial registration + national ID');
    }
    if (country === 'other') {
      return t('verification.requirements.other', 'You can verify with: passport, national ID, driver license, or any official document');
    }
    return t('verification.requirements.standard', 'You can verify with: national ID, passport, or any official document');
  }, [country, saudiDocType, t]);

  const mutation = useMutation({
    mutationFn: async () => {
      const docB64 = documentFile ? await readAsBase64(documentFile) : '';
      const nidB64 = nationalIdFile ? await readAsBase64(nationalIdFile) : '';
      const proofB64 = usernameProofFile ? await readAsBase64(usernameProofFile) : '';
      const faceB64 = faceProofFile ? await readAsBase64(faceProofFile) : '';
      return verificationApi.submit({
        name: fullName.trim(),
        country,
        documentType: country === 'saudi' ? (saudiDocType as SaudiDocumentType) : undefined,
        // The Discord set has no second document: the national ID is the whole
        // submission, so documentFile stays empty there.
        documentFile: saudiDocType === 'discord' ? '' : docB64,
        nationalIdFile: country === 'saudi' ? nidB64 : undefined,
        usernameProofFile: needsUsernameProof ? proofB64 : undefined,
        faceProofFile: needsUsernameProof && faceB64 ? faceB64 : undefined,
      });
    },
    onSuccess: () => {
      setJustSubmitted(true);
      setShowRetryForm(false);
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      toast('success', t('verification.submitSuccess', 'Your verification request has been submitted.'));
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : t('verification.submitError', 'Submission failed. Please review the form and try again.');
      toast('error', message);
    },
  });

  const handleSubmit = () => {
    if (!fullName.trim()) {
      toast('error', t('verification.nameRequired', 'Please enter your full name.'));
      return;
    }
    if (!agree) {
      toast('error', t('verification.agreeRequired', 'You must accept the terms and conditions.'));
      return;
    }
    if (country === 'saudi' && !saudiDocType) {
      toast('error', t('verification.docTypeRequired', 'Please pick a document type.'));
      return;
    }
    // The Discord set is national ID only, so there is no separate document.
    if (saudiDocType !== 'discord' && !documentFile) {
      toast('error', t('verification.documentRequired', 'Please attach your verification document.'));
      return;
    }
    if (needsUsernameProof && !usernameProofFile) {
      toast('error', t('verification.usernameProofRequired', 'Please attach the photo showing your username.'));
      return;
    }
    // The Discord path is one photo: the ID and the username in a single shot.
    if (country === 'saudi' && saudiDocType !== 'discord' && !nationalIdFile) {
      toast('error', t('verification.nationalIdRequired', 'Please attach your national ID.'));
      return;
    }
    if ((documentFile && documentFile.size > MAX_DOC_BYTES) || (nationalIdFile && nationalIdFile.size > MAX_DOC_BYTES) || (usernameProofFile && usernameProofFile.size > MAX_DOC_BYTES) || (faceProofFile && faceProofFile.size > MAX_DOC_BYTES)) {
      toast('error', t('verification.fileTooLarge', 'Each file must be under 8MB.'));
      return;
    }
    mutation.mutate();
  };

  const selectedCountry = COUNTRIES.find((c) => c.value === country) ?? COUNTRIES[0];

  return (
    <div className="mx-auto max-w-xl space-y-6 pb-12">
      {/* Header */}
      <div className="text-center pt-2">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10">
          <Shield className="h-7 w-7 text-accent" />
        </div>
        <h1 className="text-xl font-bold">{t('verification.title', 'Account verification')}</h1>
        <p className="mt-1.5 text-sm text-text-muted">
          {t('verification.subtitle', 'Verify your identity to activate your store.')}
        </p>
      </div>

      {currentStore?.testing && status !== 'verified' && <TestingBanner />}

      {status === 'blocked' && <StatusCard kind="blocked" />}
      {status === 'pending' && <StatusCard kind="pending" />}
      {status === 'verified' && <StatusCard kind="verified" />}
      {status === 'failed' && (
        <StatusCard
          kind="failed"
          reason={user?.verifcationFailureReason}
          onRetry={() => setShowRetryForm(true)}
        />
      )}

      {status === 'form' && (
        <>
          {/* Step indicator */}
          <div className="flex justify-center gap-2" dir="ltr">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className={clsx(
                  'h-1 w-8 rounded-full transition-colors',
                  step > n ? 'bg-accent/40' : step === n ? 'bg-accent' : 'bg-border-default',
                )}
              />
            ))}
          </div>

          <div className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-5">
            {/* Country */}
            <div>
              <label className="mb-2 block text-sm font-semibold">{t('verification.country', 'Country')}</label>
              <div className="relative" ref={countryWrapRef}>
                <button
                  type="button"
                  onClick={() => setCountryOpen((o) => !o)}
                  className="flex w-full items-center justify-between rounded-lg border border-border-default bg-bg-field px-4 py-3 text-sm transition-colors hover:border-accent/40"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-lg">{selectedCountry.flag}</span>
                    <span>{t(selectedCountry.nameKey, selectedCountry.value)}</span>
                  </span>
                  <ChevronDown className={clsx('h-4 w-4 text-text-muted transition-transform', countryOpen && 'rotate-180')} />
                </button>
                {countryOpen && (
                  <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-60 overflow-y-auto rounded-lg border border-border-default bg-bg-secondary shadow-lg">
                    {COUNTRIES.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => {
                          setCountry(c.value);
                          setCountryOpen(false);
                          setSaudiDocType('');
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-start text-sm transition-colors hover:bg-bg-hover"
                      >
                        <span className="text-base">{c.flag}</span>
                        <span>{t(c.nameKey, c.value)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Non-Saudi payment notice */}
            {country !== 'saudi' && (
              <div className="rounded-lg border border-warning/25 bg-warning/8 px-4 py-3 text-center text-xs leading-relaxed text-text-secondary">
                <strong className="block mb-1 text-sm text-warning">
                  {t('verification.thirdPartyTitle', 'Payments via third-party gateway')}
                </strong>
                {t('verification.thirdPartyBody', 'Payments will go through an external gateway that you connect yourself (PayTabs, Tap, etc.).')}
              </div>
            )}

            {/* Saudi doc type cards */}
            {country === 'saudi' && (
              <div>
                <label className="mb-2 block text-sm font-semibold">
                  {t('verification.documentType', 'Document type')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {/* Discord accounts: national ID only. Shown first because it
                      is their default, but the full document sets stay open. */}
                  {canUseDiscordPath && (
                    <DocTypeCard
                      icon="💬"
                      label={t('verification.docCards.discord', 'National ID only (Discord)')}
                      selected={saudiDocType === 'discord'}
                      onClick={() => setSaudiDocType('discord')}
                    />
                  )}
                  <DocTypeCard
                    icon="📄"
                    label={t('verification.docCards.freelance', 'Freelance license + ID')}
                    selected={saudiDocType === 'freelance'}
                    onClick={() => setSaudiDocType('freelance')}
                  />
                  <DocTypeCard
                    icon="🏢"
                    label={t('verification.docCards.commercial', 'Commercial registration + ID')}
                    selected={saudiDocType === 'commercial'}
                    onClick={() => setSaudiDocType('commercial')}
                  />
                </div>
              </div>
            )}

            {/* Document fields */}
            {showDocumentFields && (
              <>
                <div className="rounded-lg bg-accent/8 px-4 py-2.5 text-sm font-semibold leading-relaxed">
                  {requirementsText}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">
                    {t('verification.fullName', 'Full legal name')}
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={t('verification.fullNamePlaceholder', 'Name as it appears on the document')}
                    className="w-full rounded-lg border border-border-default bg-bg-field px-4 py-3 text-sm transition-colors placeholder:text-text-muted focus:border-accent focus:outline-none"
                  />
                </div>

                {country === 'saudi' && saudiDocType === 'freelance' && (
                  <>
                    <DocUpload
                      label={t('verification.uploads.freelanceDoc', 'Freelance license image')}
                      file={documentFile}
                      onChange={setDocumentFile}
                    />
                    <DocUpload
                      label={t('verification.uploads.nationalId', 'National ID image')}
                      file={nationalIdFile}
                      onChange={setNationalIdFile}
                    />
                  </>
                )}

                {country === 'saudi' && saudiDocType === 'commercial' && (
                  <>
                    <DocUpload
                      label={t('verification.uploads.commercialDoc', 'Commercial registration image')}
                      file={documentFile}
                      onChange={setDocumentFile}
                    />
                    <DocUpload
                      label={t('verification.uploads.nationalId', 'National ID image')}
                      file={nationalIdFile}
                      onChange={setNationalIdFile}
                    />
                  </>
                )}

                {country !== 'saudi' && (
                  <DocUpload
                    label={t('verification.uploads.generalDoc', 'Verification document image')}
                    file={documentFile}
                    onChange={setDocumentFile}
                  />
                )}

                {/* Username proof. The handle is shown back to them verbatim,
                    because the reviewer matches the photo against exactly this
                    string and a guess (display name, email) will be rejected. */}
                {needsUsernameProof && (
                  <div className="rounded-xl border border-accent/25 bg-accent/8 p-4">
                    <div className="mb-1 text-sm font-semibold">
                      {t('verification.usernameProof.title', 'Photo with your username')}
                    </div>
                    <p className="mb-3 text-xs leading-relaxed text-text-secondary">
                      {t('verification.usernameProof.help', 'Write the username below on a piece of paper (or show it on a screen), and take one photo of it next to your document, with your face visible.')}
                    </p>

                    {/* The Zero username, not the Discord handle. For Discord
                        signups it is a random slug they have never seen, which
                        is exactly what makes it proof of account ownership, so
                        it has to be impossible to miss or mistype. */}
                    <div className="mb-3 rounded-xl border-2 border-accent/40 bg-bg-primary px-4 py-3.5">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-accent">
                        {t('verification.usernameProof.label', 'Your Zero username, write exactly this')}
                      </div>
                      <div
                        dir="ltr"
                        className="mt-1.5 select-all break-all text-left font-mono text-xl font-extrabold tracking-[0.08em] text-text-primary"
                      >
                        {proofLabel || '...'}
                      </div>
                      <div className="mt-1.5 text-[11px] text-text-muted">
                        {t('verification.usernameProof.note', 'Write it on paper or show it on a screen, and copy it character by character.')}
                      </div>
                    </div>


                    <DocUpload
                      label={t('verification.uploads.usernameProof', 'Photo with the username')}
                      file={usernameProofFile}
                      onChange={setUsernameProofFile}
                    />

                    {/* The face gets its own photo. Document + username + face
                        in one frame is hard to hold and shoot, and the document
                        usually ends up unreadable — which is the whole reason
                        this second, optional upload exists. */}
                    <div className="mt-4 border-t border-border-default pt-4">
                      <p className="mb-2 text-[11px] leading-relaxed text-text-muted">
                        {t('verification.usernameProof.face', 'Optional: a second photo of you holding your document. It speeds up review, and we may ask for it later if we need to confirm.')}
                      </p>
                      <DocUpload
                        label={t('verification.uploads.faceProof', 'Photo of you holding the document (optional)')}
                        file={faceProofFile}
                        onChange={setFaceProofFile}
                      />
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 text-xs leading-relaxed text-text-secondary">
                  <h3 className="mb-2 text-sm font-semibold text-text-primary">
                    {t('verification.instructionsTitle', 'Instructions')}
                  </h3>
                  <ul className="ms-4 list-disc space-y-1">
                    {country !== 'saudi' && (
                      <li>{t('verification.instructions.handWritten', 'Write your username on a paper next to the document in the photo')}</li>
                    )}
                    <li>{t('verification.instructions.notExpired', 'Make sure the document is not expired')}</li>
                    <li>{t('verification.instructions.readable', 'Make sure the information is clear and readable')}</li>
                  </ul>
                  <p className="mt-2 text-[11px] text-text-muted">
                    {t('verification.instructions.privacy', 'This information is protected and confidential. We ask for it to keep the platform and its users safe.')}
                  </p>
                </div>

                {/* Agree + submit */}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    className="h-4 w-4 accent-accent"
                  />
                  <span>
                    {t('verification.agreePrefix', 'I agree to the ')}
                    <a
                      href="https://00pays.com/sellerTerms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                    >
                      {t('verification.terms', 'terms and conditions')}
                    </a>
                  </span>
                </label>

                <Button
                  onClick={handleSubmit}
                  isLoading={mutation.isPending}
                  className="w-full"
                  size="lg"
                >
                  {t('verification.submit', 'Submit verification request')}
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DocTypeCard({ icon, label, selected, onClick }: { icon: string; label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex flex-col items-center justify-center rounded-xl border p-4 text-center transition-colors',
        selected ? 'border-accent bg-accent/8' : 'border-border-default hover:border-accent/40',
      )}
    >
      <div className="mb-2 text-2xl">{icon}</div>
      <div className="text-xs font-semibold leading-tight">{label}</div>
    </button>
  );
}

function DocUpload({ label, file, onChange }: { label: string; file: File | null; onChange: (file: File | null) => void }) {
  const { t } = useTranslation();
  const id = useMemo(() => `vupload-${Math.random().toString(36).slice(2, 9)}`, []);
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold">{label}</label>
      <label
        htmlFor={id}
        className={clsx(
          'flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-4 text-sm transition-colors',
          file
            ? 'border-accent bg-accent/5 text-text-primary'
            : 'border-border-default bg-bg-field text-text-muted hover:border-accent/40 hover:bg-accent/3',
        )}
      >
        {file ? <Check className="h-5 w-5 text-accent" /> : <Upload className="h-5 w-5" />}
        <span>{file ? t('verification.fileSelected', 'File selected') : t('verification.pickFile', 'Choose a file')}</span>
        <input
          id={id}
          type="file"
          accept={ALLOWED_MIME}
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        />
      </label>
      {file && (
        <p className="mt-1 break-all text-center text-[11px] text-text-muted">{file.name}</p>
      )}
    </div>
  );
}

function TestingBanner() {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 p-4">
      <FlaskConical className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
      <div className="text-sm">
        <p className="font-semibold text-text-primary">
          {t('verification.testingBannerTitle', 'Your store is in testing mode')}
        </p>
        <p className="mt-0.5 text-text-secondary">
          {t('verification.testingBannerBody', 'Your store stays in testing mode until your identity is verified.')}
        </p>
      </div>
    </div>
  );
}

function StatusCard({ kind, reason, onRetry }: { kind: 'verified' | 'pending' | 'failed' | 'blocked'; reason?: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  const config = {
    verified: {
      Icon: Shield,
      tone: 'bg-success/10 text-success',
      title: t('verification.status.verifiedTitle', 'Your account is verified'),
      body: t('verification.status.verifiedBody', 'Every store you own is fully activated.'),
    },
    pending: {
      Icon: Clock,
      tone: 'bg-info/10 text-info',
      title: t('verification.status.pendingTitle', 'Your request is under review'),
      body: t('verification.status.pendingBody', 'We received your documents. Usually verified within 1–24 hours.'),
    },
    failed: {
      Icon: AlertTriangle,
      tone: 'bg-warning/10 text-warning',
      title: t('verification.status.failedTitle', 'Verification rejected'),
      body: reason || t('verification.status.failedBody', 'Please review the issue and resubmit.'),
    },
    blocked: {
      Icon: Ban,
      tone: 'bg-danger/10 text-danger',
      title: t('verification.status.blockedTitle', 'Submissions blocked'),
      body: t('verification.status.blockedBody', 'Your account is blocked from submitting verification. Contact support.'),
    },
  }[kind];
  const { Icon } = config;
  return (
    <div className="rounded-xl border border-border-default bg-bg-secondary p-8 text-center">
      <div className={clsx('mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl', config.tone)}>
        <Icon className="h-8 w-8" />
      </div>
      <h2 className="text-lg font-bold">{config.title}</h2>
      <p className="mt-2 text-sm text-text-muted leading-relaxed">{config.body}</p>
      {kind === 'failed' && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 rounded-lg border border-border-default px-6 py-2.5 text-sm font-semibold transition-colors hover:border-accent/50"
        >
          {t('verification.retry', 'Try again')}
        </button>
      )}
    </div>
  );
}
